/**
 * 型定義(Phase 1: ユーザー・マスタ)。正となる仕様: docs/specs/cc.md §3・§4・§9
 * オペレーターマスタの項目は 2026-09-09 の発注者指示(スタッフ項目表)に合わせて改訂。
 * 金額は整数(円)、日付はJSTの 'YYYY-MM-DD' 文字列。
 */
import type { Timestamp } from 'firebase/firestore';

export type DateStr = string; // 'YYYY-MM-DD'
export type MonthStr = string; // 'YYYY-MM'
export type Yen = number; // 整数(円)

// ---- ロール(§3) -----------------------------------------------------------

export type Role = 'admin' | 'operator' | 'viewer';

export const ROLE_LABELS: Record<Role, string> = {
  admin: '管理者',
  operator: 'オペレーター',
  viewer: '閲覧',
};

export type UserDoc = {
  id: string; // Firebase Auth uid
  email: string;
  name: string;
  role: Role;
  active: boolean;
  /** operator ロールのとき operators のdocIdに紐付ける(本人成績ページ用。Phase 4) */
  op_id: string | null;
  created_at?: unknown;
  updated_at?: unknown;
};

// ---- 架電内容(給湯器 / 不用品) --------------------------------------------

/**
 * 架電内容。コードは A=給湯器 / B=不用品(2026-09-17 MTG)。**C 以降は未定**。
 * 増やすときはこの型・BUSINESS_LABELS・BUSINESS_CODES に1件ずつ足す(型が足りない箇所をコンパイルエラーで教えてくれる。
 * 採番ルール `settings/bb_numbering` と RANK の換算も合わせて決める)
 */
export type BusinessType = 'kyutoki' | 'fuyohin';

export const BUSINESS_LABELS: Record<BusinessType, string> = {
  kyutoki: '給湯器',
  fuyohin: '不用品',
};
export const BUSINESS_CODES: Record<BusinessType, string> = { kyutoki: 'A', fuyohin: 'B' };

// ---- オペレーターマスタ(§4.1 + 2026-09-09 スタッフ項目表) ------------------

/** 在籍: 在籍／退職／休職／HMT／移行(HMT・移行は 2026-09-09 追加) */
export type OperatorStatus = 'active' | 'retired' | 'leave' | 'hmt' | 'transfer';
export const OPERATOR_STATUS_LABELS: Record<OperatorStatus, string> = {
  active: '在籍',
  retired: '退職',
  leave: '休職',
  hmt: 'HMT',
  transfer: '移行',
};
export const OPERATOR_STATUSES = Object.keys(OPERATOR_STATUS_LABELS) as OperatorStatus[];

/** 出勤形態: 管理／出勤／在宅／タイミー(画面の名前は 2026-09-17 に「品川」→「出勤」。保存値は shinagawa のまま) */
export type WorkStyle = 'kanri' | 'shinagawa' | 'zaitaku' | 'timee';
export const WORK_STYLE_LABELS: Record<WorkStyle, string> = {
  kanri: '管理',
  shinagawa: '出勤',
  zaitaku: '在宅',
  timee: 'タイミー',
};
export const WORK_STYLES = Object.keys(WORK_STYLE_LABELS) as WorkStyle[];

/** 報酬区分: 時給／歩合。金額欄の文言はこれで切り替える(時給 / 件数単価) */
export type PayType = 'hourly' | 'commission';
export const PAY_TYPE_LABELS: Record<PayType, string> = {
  hourly: '時給',
  commission: '歩合',
};
export const PAY_TYPES = Object.keys(PAY_TYPE_LABELS) as PayType[];

// ---- コード体系(2026-09-17 MTG で確定) -------------------------------------------
// 出勤形態 × 報酬区分 の組み合わせで報酬計算式が決まる(A×H=出勤時給、H×C=在宅歩合、H×H=在宅時給、M×H=管理者、T×H=タイミー)。
// **保存している値(kanri / shinagawa ほか)は変えず**、画面とCSVで使う1文字コードとして対応させる(日次集計のデータ契約を壊さないため)

/** 出勤形態のコード: M=管理 / A=出勤(品川) / H=在宅 / T=タイミー */
export const WORK_STYLE_CODES: Record<WorkStyle, string> = { kanri: 'M', shinagawa: 'A', zaitaku: 'H', timee: 'T' };
/** 報酬区分のコード: H=時給(hour) / C=歩合(commission) */
export const PAY_TYPE_CODES: Record<PayType, string> = { hourly: 'H', commission: 'C' };
/** 画面でコードと一緒に出す名前 */
export const WORK_STYLE_CODE_LABELS: Record<WorkStyle, string> = { kanri: '管理', shinagawa: '出勤', zaitaku: '在宅', timee: 'タイミー' };

/** 「A×H」のような組み合わせコード */
export function styleCode(workStyle: WorkStyle, payType: PayType): string {
  return `${WORK_STYLE_CODES[workStyle] ?? '?'}×${PAY_TYPE_CODES[payType] ?? '?'}`;
}

/** インボイス控除の区分(在宅スタッフのみ。2026-09-17 MTG)。**控除率・手数料額は未確定のため区分を持つだけで計算しない** */
export type InvoiceType = 'invoice_no_fee' | 'no_invoice_fee' | 'no_invoice_no_fee';
export const INVOICE_TYPE_LABELS: Record<InvoiceType, string> = {
  invoice_no_fee: 'インボイスあり／手数料なし',
  no_invoice_fee: 'インボイスなし／手数料あり',
  no_invoice_no_fee: 'インボイスなし／手数料なし',
};
export const INVOICE_TYPES = Object.keys(INVOICE_TYPE_LABELS) as InvoiceType[];

/** 報酬区分に応じた金額欄の名前 */
export function wageLabel(payType: PayType): string {
  return payType === 'hourly' ? '時給' : '件数単価';
}

/**
 * 架電アカウント。1人が複数のBB ID(給湯器用 / 不用品用)を持つため配列。
 * BB ID・内線番号はシステム内で生成する(ゆくゆくはAPIでBlueBeanへ自動反映)。
 */
/** 時給／単価の改訂1件。NEXUS Gate の金額改定(amount_history)と同じ持ち方 */
export type WageRevision = {
  wage: Yen;
  effective_from: DateStr; // 適用開始日
  reason: string;
  changed_by: string; // users のdocId
  changed_at: Timestamp;
};

/** 交通費(往復)の改訂1件(2026-09-09 ③指示で新設) */
export type CommuteRevision = {
  amount: Yen;
  effective_from: DateStr;
  reason: string;
  changed_by: string;
  changed_at: Timestamp;
};

/**
 * 架電アカウント(BB ID)。報酬は出勤形態×報酬区分で異なるため **BB ID単位** に持つ(2026-09-09 ③指示)。
 * 同じBB IDの中で昇格・降格により金額が改訂されていく → wage_history に履歴を残す
 */
export type BbAccount = {
  /** 出勤形態・報酬区分はBB ID単位(品川用SNと在宅用SRを両方持つ人がいるため)。人の値は先頭アカウントを写す */
  work_style: WorkStyle;
  pay_type: PayType;
  bb_id: string;
  ext: string | null; // 内線番号 = BB IDの連番の数字
  company_id: string | null; // 架電会社(companies のdocId)
  business: BusinessType; // 架電内容
  active: boolean;
  /** このBB IDの現在の時給 or 件数単価。改訂は wage_history に必ず残す */
  current_wage: Yen;
  wage_history: WageRevision[];
};

/** その他情報(Googleフォームの情報)。項目名と値の自由な組(＋／−で増減) */
export type ExtraInfo = { label: string; value: string };

export type Operator = {
  id: string; // docId(自動)
  op_code: string; // 管理番号(OP-0001。システム自動付与)
  name: string;
  bb_accounts: BbAccount[];
  status: OperatorStatus;
  /** 先頭アカウントの出勤形態・報酬区分(一覧・フィルタ用) */
  work_style: WorkStyle;
  pay_type: PayType;
  /** 現在の往復交通費(品川のみ。P-38)。改訂は commute_history に必ず残す */
  commute_cost: Yen;
  commute_history: CommuteRevision[];
  /** インボイス控除の区分(在宅のみ。未設定は null。計算には使わない: 2026-09-17 MTG) */
  invoice_type?: InvoiceType | null;
  /** PCレンタル あり/なし(2026-09-17 MTG)。未設定は なし */
  pc_rental?: boolean;
  /** 本人が成績表を閲覧できるか(2026-09-17 MTG)。スタッフページ(Phase 4)で参照する。未設定は 公開しない */
  stats_visible?: boolean;
  joined_at: DateStr | null;
  left_at: DateStr | null;
  extra_info: ExtraInfo[];
  note: string | null;
  /** operator ロールのログインに紐付ける uid(Phase 4 で利用) */
  uid: string | null;
  created_by?: string;
  created_at?: unknown;
  updated_by?: string;
  updated_at?: unknown;
};

/** ある日に適用される時給/単価を改訂履歴から求める(履歴が無ければ現在値) */
export function wageOn(account: Pick<BbAccount, 'current_wage' | 'wage_history'>, date: DateStr): Yen {
  const applicable = (account.wage_history ?? [])
    .filter((h) => h.effective_from <= date)
    .sort((a, b) => (a.effective_from < b.effective_from ? -1 : 1));
  return applicable.length > 0 ? applicable[applicable.length - 1]!.wage : account.current_wage;
}

/** ある日に適用される往復交通費を改訂履歴から求める */
export function commuteOn(operator: Pick<Operator, 'commute_cost' | 'commute_history'>, date: DateStr): Yen {
  const applicable = (operator.commute_history ?? [])
    .filter((h) => h.effective_from <= date)
    .sort((a, b) => (a.effective_from < b.effective_from ? -1 : 1));
  return applicable.length > 0 ? applicable[applicable.length - 1]!.amount : operator.commute_cost;
}

// ---- 会社マスタ(§4.2) -----------------------------------------------------

export type CompanyStatus = 'active' | 'inactive';
export const COMPANY_STATUS_LABELS: Record<CompanyStatus, string> = {
  active: '取引中',
  inactive: '停止',
};

/** 会社単価の改訂1件 */
export type PriceRevision = {
  price: Yen;
  effective_from: DateStr;
  reason: string;
  changed_by: string;
  changed_at: Timestamp;
};

export type Company = {
  id: string; // docId = CL-001 形式
  name: string;
  /** 会社単価(納品1件あたりの売上単価) */
  unit_price: Yen;
  price_history: PriceRevision[];
  product_type: BusinessType;
  delivery_timing: string; // 前日 / 土曜日 等
  /** BBの業務コード紐付け(対応履歴の「登録者名/業務」との突合キー) */
  bb_business_codes: string[];
  phone: string | null;
  status: CompanyStatus;
  note: string | null;
  created_by?: string;
  created_at?: unknown;
  updated_by?: string;
  updated_at?: unknown;
};

export function priceOn(company: Pick<Company, 'unit_price' | 'price_history'>, date: DateStr): Yen {
  const applicable = (company.price_history ?? [])
    .filter((h) => h.effective_from <= date)
    .sort((a, b) => (a.effective_from < b.effective_from ? -1 : 1));
  return applicable.length > 0 ? applicable[applicable.length - 1]!.price : company.unit_price;
}

// ---- 監査ログ(登録・編集ログ。NEXUS Gate と同じ形) ------------------------

export type AuditTargetType = 'operator' | 'company' | 'user' | 'import' | 'bb_credential';

export type AuditLog = {
  id: string;
  target_type: AuditTargetType;
  target_id: string;
  action: string;
  by: string;
  at: Timestamp;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
};

// ---- システム設定(BB ID・内線番号の生成ルール) ----------------------------
// 発注者指示(2026-09-09 ②): BB ID = 出勤形態＋連番、内線番号 = その連番の数字
//   品川: SN＋1000〜 / 在宅時給: SR＋2000〜 / 在宅歩合: RS＋4000〜 / タイミー: STM＋001〜(タイミーは時給のみ)
//   管理は指示に無いため現行シートの SNM＋01〜 を仮置き(要確認)

/** 出勤形態×報酬区分の組(タイミーは時給のみ) */
export type BbStyleKey = 'shinagawa' | 'zaitaku_hourly' | 'zaitaku_commission' | 'timee' | 'kanri';
export const BB_STYLE_LABELS: Record<BbStyleKey, string> = {
  shinagawa: '出勤×時給',
  zaitaku_hourly: '在宅×時給',
  zaitaku_commission: '在宅×歩合',
  timee: 'タイミー×時給',
  kanri: '管理×時給',
};
export const BB_STYLE_KEYS = Object.keys(BB_STYLE_LABELS) as BbStyleKey[];

/** 形態コードのキー: 出勤形態×報酬区分×架電内容(2026-09-09 ⑧指示で架電内容を追加) */
export type BbPrefixKey = `${BbStyleKey}_${BusinessType}`;
export function bbPrefixKey(style: BbStyleKey, business: BusinessType): BbPrefixKey {
  return `${style}_${business}`;
}

export const BB_PREFIX_LABELS: Record<BbPrefixKey, string> = Object.fromEntries(
  BB_STYLE_KEYS.flatMap((s) => (Object.keys(BUSINESS_LABELS) as BusinessType[]).map((b) => [bbPrefixKey(s, b), `${BB_STYLE_LABELS[s]}×${BUSINESS_LABELS[b]}`])),
) as Record<BbPrefixKey, string>;

export const BB_PREFIX_KEYS = Object.keys(BB_PREFIX_LABELS) as BbPrefixKey[];

export type BbPrefixRule = {
  prefix: string; // 例 SN
  start: number; // 採番開始値(既存IDがあればその最大+1から続ける)
  digits: number; // 桁数(ゼロ埋め)
};

export type BbNumberingSettings = {
  rules: Record<BbPrefixKey, BbPrefixRule>;
};

/**
 * 既定の形態コード。給湯器は②指示(品川 SN1000〜 / 在宅時給 SR2000〜 / 在宅歩合 RS4000〜 / タイミー STM001〜)。
 * 不用品(ホワイト)のコードは指示に無いため、給湯器のコード＋W(現行BlueBeanの表示名「○○W」に合わせた仮置き)。
 * 管理は現行シートの SNM＋01〜 を仮置き。いずれもシステム設定で変更できる
 */
export const DEFAULT_BB_NUMBERING: BbNumberingSettings = {
  rules: {
    shinagawa_kyutoki: { prefix: 'SN', start: 1000, digits: 4 },
    shinagawa_fuyohin: { prefix: 'SNW', start: 1000, digits: 4 },
    zaitaku_hourly_kyutoki: { prefix: 'SR', start: 2000, digits: 4 },
    zaitaku_hourly_fuyohin: { prefix: 'SRW', start: 2000, digits: 4 },
    zaitaku_commission_kyutoki: { prefix: 'RS', start: 4000, digits: 4 },
    zaitaku_commission_fuyohin: { prefix: 'RSW', start: 4000, digits: 4 },
    timee_kyutoki: { prefix: 'STM', start: 1, digits: 3 },
    timee_fuyohin: { prefix: 'STMW', start: 1, digits: 3 },
    kanri_kyutoki: { prefix: 'SNM', start: 1, digits: 2 },
    kanri_fuyohin: { prefix: 'SNMW', start: 1, digits: 2 },
  },
};

// ---- BlueBean ログイン情報(BB ID／パスワード一覧) ---------------------------
// スタッフ個人のBlueBeanログイン用。admin のみ読み書き(viewer・operator には見せない)。
// ※システム自体のBlueBean API認証情報は Functions Secret で管理し、ここには入れない

export type BbCredential = {
  id: string; // = bb_id
  bb_id: string;
  op_id: string | null;
  password: string;
  note: string | null;
  updated_by?: string;
  updated_at?: unknown;
};

// ---- 日次集計スナップショット(§9 daily_summary)。日次/月次ダッシュボードの元データ ----
// Phase 2 の日次集計エンジン(BlueBean取込＋品川勤怠＋割振)がここへ書く。
// 現行シート「日次集計」「出勤表 報酬版」「スタッフ別」「原価率」を再現できる粒度で持つ。

/** 時間帯別取得数のバンド(9時台〜19時台の11本) */
export const HOUR_BANDS = [9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19] as const;

/** 現行シートの「種別」(品川 / 在宅時給 / 歩合 / ホワイト / 管理者) */
export type StaffCategory = 'shinagawa' | 'zaitaku_jikyu' | 'buai' | 'white' | 'kanrisha';
export const STAFF_CATEGORY_LABELS: Record<StaffCategory, string> = {
  shinagawa: '出勤/時給',
  zaitaku_jikyu: '在宅/時給',
  buai: '在宅/歩合',
  white: 'ホワイト(不用品)',
  kanrisha: '管理者',
};

/** 「品川/時給」のような出勤形態×報酬区分の表記 */
export function styleLabel(workStyle: WorkStyle, payType: PayType): string {
  return `${WORK_STYLE_LABELS[workStyle] ?? '—'}/${PAY_TYPE_LABELS[payType] ?? '—'}`;
}

/**
 * スタッフの1日分。1行 = BB ID × 割振会社(架電会社)。
 * 同じBB IDが複数の会社に架電した日は会社ごとに行が分かれる(日次ダッシュボードで
 * 親=アポインター／子=割振会社 の階層に組む。2026-09-09 ③指示)
 */
export type StaffDaily = {
  op_id: string;
  op_code: string;
  name: string;
  bb_id: string;
  work_style: WorkStyle;
  pay_type: PayType;
  business: BusinessType;
  /** 割振(当日架電した会社)。原価配賦キー */
  company_id: string | null;
  /** 当日適用の時給 or 件数単価 */
  wage: Yen;
  appointments: number; // 取得数
  by_hour: number[]; // 時間帯別取得数(HOUR_BANDS と同じ順)
  calls: number; // 通話回数
  reception_sec: number; // 受付時間
  talk_sec: number; // 通話時間
  after_sec: number; // 後処理時間
  /** 後処理回数(OPレポート)。後処理時間の平均(AVG.)に使う。無い日は通話回数で代用 */
  after_count?: number;
  login_sec: number; // ログイン時間(管理者の報酬計算に使う)
  break_sec: number;
  away_sec: number;
  /** 品川のみ: シフト勤怠(拘束h・休憩h)。実稼働は 拘束−休憩 */
  shift_hours: number | null;
  break_hours: number | null;
  /** 実稼働(h)。品川・タイミー=拘束(ログイン時間を0.5h丸め)−休憩(休憩時間を0.5h切上げ)、在宅=BB稼働合計(受付+通話+後処理) */
  worked_hours: number;
  /** 実稼働の出どころ: bb=BB稼働(在宅・管理者) / auto=ログイン・休憩からの自動値(出勤) / manual=勤怠を手で直した(shinagawa_kintai) */
  hours_source?: 'bb' | 'auto' | 'manual';
  /** 当日の報酬(円)。形態別の計算式(§4.3)で算出済み */
  pay: Yen;
  /** 支給する交通費(品川のみ。P-38 のルールで 0/30/50/70% に減額済み)。同じ人の同じ日で1行にだけ入る */
  commute: Yen;
  /** マスタの往復交通費(減額前)。月100時間以上での再計算(P-38)に使う */
  commute_base?: Yen;
};

/** 対応履歴CSV 1ファイルの取込記録(daily_summary.sources.activity[]) */
export type ActivitySource = {
  import_id: string;
  file_name: string;
  hash: string;
  row_count: number;
  /** このファイルに含まれていた業務コード(会社)。同じ業務の再取込で差し替える単位 */
  business_codes: string[];
  appointments: number;
  imported_at?: unknown;
};

/**
 * 出勤(品川・タイミー)の勤怠の手修正(D-56)。自動値(BlueBean のログイン時間→拘束、休憩時間→休憩)を直したときだけ保存する。
 * docId = `${date}_${bb_id}`。OPレポートを再取込しても手修正は残る
 */
export type Kintai = {
  date: DateStr;
  bb_id: string;
  op_id: string;
  /** 拘束時間(h・0.5刻み) */
  shift_hours: number;
  /** 休憩(h・0.5刻み) */
  break_hours: number;
  note: string | null;
  updated_by?: string;
  updated_at?: unknown;
};

export type DailySummary = {
  id: string; // = date
  date: DateStr;
  staff: StaffDaily[];
  /** その日の会社単価(company_id → 円)。売上 = 取得数 × 割振会社の単価(実単価) */
  unit_prices: Record<string, Yen>;
  note: string | null;
  /** 取込元(BlueBean CSV)。無ければサンプル(seed) */
  sources?: {
    op_report?: { import_id: string; file_name: string; hash: string; row_count: number; imported_at?: unknown };
    /** 対応履歴は業務(会社)ごとに別ファイルで来ることがあるため配列。同じ業務を再取込したら差し替える */
    activity?: ActivitySource[];
  };
  /** 出勤(品川・タイミー)の勤怠を確認済み(D-56)。OPレポートを取り込み直すと消える(再確認) */
  kintai_check?: { at: unknown; by: string; by_name?: string | null };
  created_at?: unknown;
  updated_by?: string;
  updated_at?: unknown;
};

/**
 * 納品件数(§5.1 / §9 deliveries)。取得したアポは翌日に各会社へ納品する。
 *   取得件数 × 単価 = 売上見込 ／ 納品件数 × 単価 = 売上実績(納品前キャンセル控除後)
 * 現行「サマリ」シートの手入力(その日に納品した件数を会社別に)に相当。docId = date
 */
export type Delivery = {
  id: string; // = date(納品日)
  date: DateStr;
  counts: Record<string, number>; // company_id → 納品件数(details の内訳合計)
  /** 会社別の内訳(現行「サマリ」シート: 品川／歩合／時給の納品件数・先方アポ数・キャンセル)。無い日は counts のみ */
  details?: Record<string, DeliveryDetail>;
  note: string | null;
  updated_by?: string;
  updated_at?: unknown;
};

/** 納品の内訳キー(出勤形態×報酬区分)。現行サマリシートの 品川／歩合／時給 */
export type DeliveryStyle = 'shinagawa' | 'buai' | 'jikyu';
export const DELIVERY_STYLES: DeliveryStyle[] = ['shinagawa', 'buai', 'jikyu'];
export const DELIVERY_STYLE_LABELS: Record<DeliveryStyle, string> = {
  shinagawa: '出勤/時給',
  buai: '在宅/歩合',
  jikyu: '在宅/時給',
};

/** 人件費・交通費の内訳キー(現行サマリシート: 出勤人件費(品川)／在宅人件費(歩合・時給)／タイミー) */
export type CostStyle = 'shinagawa' | 'buai' | 'jikyu' | 'timee';
export const COST_STYLES: CostStyle[] = ['shinagawa', 'buai', 'jikyu', 'timee'];
export const COST_STYLE_LABELS: Record<CostStyle, string> = {
  shinagawa: '出勤/時給',
  buai: '在宅/歩合',
  jikyu: '在宅/時給',
  timee: 'タイミー/時給',
};

/** アポインター別の納品件数(⑨指示: いずれ納品リストとの突合で自動化) */
export type DeliveryAppointer = { name: string; style: DeliveryStyle; count: number };

export type DeliveryDetail = {
  by_style: Partial<Record<DeliveryStyle, number>>;
  /** 取得件数の手修正(出勤形態×報酬区分ごと)。無ければ日次集計(対応履歴)の値。売上見込・PL のアポ数にはこの値を使う(⑬指示) */
  appointments?: Partial<Record<DeliveryStyle, number>>;
  /** アポインター別の納品件数(op_id → 件数)。入力があれば by_style はその合計 */
  by_appointer?: Record<string, DeliveryAppointer>;
  /** 人件費(円)の入力: 出勤形態×報酬区分ごと。未入力なら日次集計の報酬(時給×稼働など)を使う */
  labor?: Partial<Record<CostStyle, number>>;
  /** 交通費(円)の入力: 出勤形態×報酬区分ごと。未入力なら日次集計の交通費を使う */
  commute?: Partial<Record<CostStyle, number>>;
  /** 先方アポ数(会社側で取得したアポ。現行「合計」シートの項目) */
  client_appointments: number;
  cancel_zaitaku: number;
  cancel_shukkin: number;
};

export function emptyDeliveryDetail(): DeliveryDetail {
  return { by_style: {}, client_appointments: 0, cancel_zaitaku: 0, cancel_shukkin: 0 };
}

// ---- 経費の科目(⑨・⑩指示: 科目①／科目② → 項目。月次設定の経費(月額)に付ける) ----

/** 経費の科目①(大分類)と科目②(中分類) */
export type ExpenseCategories = { categories: { name: string; subs: string[] }[] };

export const DEFAULT_EXPENSE_CATEGORIES: ExpenseCategories = {
  categories: [
    { name: '人件費', subs: ['報酬', '法定福利', '消費税', '通勤費', '賞与', '求人費'] },
    { name: '事務所', subs: ['家賃', '電気代', '水道代', 'Wi-Fi', '水サーバー', 'ゴミ代', '消耗品', '雑費'] },
    { name: 'システム', subs: ['BB(SUM)', 'BB(HMT)', 'BB請求分(FY)', 'BB請求分(HMT)', 'Ajシステム', 'Ajシステム料(FY)', 'Ajシステム料(HMT)', 'Aj通話料', '通話料利益(FY)', '通話料利益(HMT)', 'システム料利益(ホワイト)'] },
    { name: 'その他', subs: ['PC賃貸借費用', 'リスト購入費', 'リスト販売費', '営業外費用', '未分類'] },
  ],
};

/** 項目名から既定の科目①／科目②を引く(科目②の名前と一致すればその科目、無ければ その他／未分類) */
export function defaultExpenseCategory(name: string): { category1: string; category2: string } {
  for (const c of DEFAULT_EXPENSE_CATEGORIES.categories) if (c.subs.includes(name)) return { category1: c.name, category2: name };
  return { category1: 'その他', category2: '未分類' };
}

/** 内訳から会社の納品合計 */
export function deliveryTotal(d: DeliveryDetail): number {
  return DELIVERY_STYLES.reduce((s, k) => s + (d.by_style[k] ?? 0), 0);
}

/** 月次の目標値(§5.4 / 現行シート「日報」「原価率」の目標) */
export type MonthTargets = {
  id: string; // = 'YYYY-MM'
  daily_appointments: number; // 1日の取得目標(合計)
  weekly_appointments: number; // 1週の取得目標
  cost_rate_total: number; // 総合計原価率の目標(%)
  cost_rate_with_manager: number; // 管理者あり合計の目標(%)
  cost_rate_fuyohin: number; // 不用品(ホワイト)管理者ありの目標(%)
  /** 割振が無い行の売上換算に使う単価(現行シートの加重平均単価) */
  fallback_unit_price: { kyutoki: Yen; fuyohin: Yen };
  /** 管理者別の1日目標(op_id → 件数) */
  manager_targets: Record<string, number>;

  // ---- PL(現行「PL日報」の前提値。1日シート・サマリシートより) ----
  /** 営業日数(未設定なら暦日数)。経費の日割りに使う */
  business_days: number | null;
  /** キャンセル分 = 売上 × キャンセル率(%)。現行は 0.5% */
  cancel_rate: number;
  /** Aj通話料 = 通話時間(分) × 通話料単価(円/分)。現行は 2.88 */
  call_rate_per_min: number;
  /** 月額の経費項目(報酬・法定福利・家賃 … )。日割り(÷暦日数)し構成比で会社へ配賦 */
  expenses: PlExpense[];
  /** 会社別のアポ数目標(件/日) */
  company_targets: Record<string, number>;
  /** 営業外経費「管理者報酬」= 営業利益 × 率(%)。現行「1日」シートは 10% と 5% の2行(D-51) */
  manager_bonus_rates: number[];
  /** タイミー振込手数料 = その日のタイミー稼働人数 × この金額(円)。現行 220円(D-52) */
  timee_fee_per_person: number;
};

/** 月額の経費1項目。科目①／科目②(⑩指示)は未設定なら項目名から既定を引く(defaultExpenseCategory) */
export type PlExpense = { name: string; amount: Yen; category1?: string; category2?: string };

/** 現行サマリシートの経費項目(名称のみ。金額は月次設定で入力) */
export const DEFAULT_EXPENSE_NAMES = [
  '報酬',
  '法定福利',
  '消費税',
  '通勤費',
  '賞与',
  '求人費',
  '家賃',
  '電気代',
  '水道代',
  'Wi-Fi',
  '水サーバー',
  'ゴミ代',
  '消耗品',
  '雑費',
  'BB(SUM)',
  'BB(HMT)',
  'BB請求分(FY)',
  'BB請求分(HMT)',
  'Ajシステム',
  'Ajシステム料(FY)',
  'Ajシステム料(HMT)',
  '通話料利益(FY)',
  '通話料利益(HMT)',
  'システム料利益(ホワイト)',
  'PC賃貸借費用',
  'リスト購入費',
  'リスト販売費',
] as const;

export const DEFAULT_MONTH_TARGETS: Omit<MonthTargets, 'id'> = {
  daily_appointments: 50,
  weekly_appointments: 215,
  cost_rate_total: 44.5,
  cost_rate_with_manager: 39.5,
  cost_rate_fuyohin: 67,
  fallback_unit_price: { kyutoki: 1469, fuyohin: 5000 },
  manager_targets: {},
  business_days: null,
  cancel_rate: 0.5,
  call_rate_per_min: 2.88,
  expenses: [],
  company_targets: {},
  manager_bonus_rates: [10, 5],
  timee_fee_per_person: 220,
};
